import React, { useRef, useEffect, useState, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { ArrowLeft, ShoppingBag, CreditCard, Calendar, Gamepad2, Receipt, Search, CheckCircle2, Clock, AlertCircle, Package, Star, Loader2, ChevronRight } from 'lucide-react';
import { FaWindows, FaApple, FaXbox } from 'react-icons/fa';
import { DiAndroid } from 'react-icons/di';
import { SiOculus, SiPlaystation, SiSteamdeck } from 'react-icons/si';
import { BsNintendoSwitch } from 'react-icons/bs';
import { TbDeviceVisionProFilled } from 'react-icons/tb';
import gsap from 'gsap';
import toast from 'react-hot-toast';
import { downloadInvoicePdf } from '../services/invoiceService';
import { fetchMyOrders } from '../redux/slice/order.slice';
import { submitReview, fetchUserReviews, updateReview } from '../redux/slice/review.slice';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import orderAstronautImg from '../assets/images/order.webp';

const PlatformIconMap = {
    windows: FaWindows,
    mac: FaApple,
    ios: FaApple,
    playstation: SiPlaystation,
    xbox: FaXbox,
    nintendo: BsNintendoSwitch,
    quest: SiOculus,
    android: DiAndroid,
    visionpro: TbDeviceVisionProFilled,
    steamdeck: SiSteamdeck
};

const PlatformNameMap = {
    windows: 'Windows',
    mac: 'macOS',
    ios: 'iOS',
    playstation: 'PlayStation',
    xbox: 'Xbox',
    nintendo: 'Nintendo',
    quest: 'Meta Quest',
    android: 'Android',
    visionpro: 'Vision Pro',
    steamdeck: 'Steam Deck'
};

const PlatformColorMap = {
    windows: '#0078d7',
    mac: '#a3aaae',
    ios: '#a3aaae',
    playstation: '#3b82f6',
    xbox: '#107c10',
    nintendo: '#e60012',
    quest: '#00f0ff',
    android: '#3ddc84',
    visionpro: '#a855f7',
    steamdeck: '#60a5fa'
};

const ReviewSchema = Yup.object().shape({
    rating: Yup.number().min(1, 'Rating must be at least 1 star').max(5).required('Rating is required'),
    comment: Yup.string().required('Review comment is required').min(5, 'Review must be at least 5 characters long')
});

// Responsive Order Card Skeleton Loader matching exact card structure
const OrderCardSkeleton = () => (
    <div className="relative rounded-2xl border border-white/10 bg-[#080814]/85 backdrop-blur-xl p-3.5 xs:p-4 md:p-6 flex flex-col gap-5 sm:gap-6 cyber-grid-card animate-pulse overflow-hidden">
        {/* Tech corner accent line */}
        <div className="absolute top-0 left-0 w-full h-[2px] bg-gradient-to-r from-cyber-cyan/30 via-cyber-blue/20 to-cyber-purple/20"></div>

        {/* Order Details Header Skeleton */}
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3.5 sm:gap-6 pb-4 sm:pb-5 border-b border-white/10 bg-white/[0.005]">
            {/* Left side */}
            <div className="flex flex-col gap-2 xs:gap-2.5 min-w-0">
                <div className="flex items-center gap-2 xs:gap-2.5">
                    <div className="h-5 w-24 xs:w-28 rounded-lg bg-white/5 border border-white/5"></div>
                    <div className="h-[1px] w-6 xs:w-8 bg-white/5"></div>
                </div>
                <div className="flex flex-wrap items-center gap-2 xs:gap-2.5 sm:gap-3">
                    <div className="h-7 sm:h-8 w-28 xs:w-36 rounded-xl bg-white/5 border border-white/5"></div>
                    <div className="h-7 sm:h-8 w-32 xs:w-44 rounded-xl bg-white/5 border border-white/5"></div>
                </div>
            </div>

            {/* Right side */}
            <div className="flex flex-col items-start lg:items-end gap-2 xs:gap-2.5">
                <div className="h-5 w-20 xs:w-24 rounded-md bg-white/5 border border-white/5"></div>
                <div className="flex items-center gap-3.5 sm:gap-5">
                    <div className="h-7 sm:h-8 w-16 xs:w-20 rounded-lg bg-white/5"></div>
                    <div className="h-7 sm:h-8 w-20 xs:w-24 rounded-xl bg-white/5 border border-white/5"></div>
                </div>
            </div>
        </div>

        {/* Order Items Skeleton */}
        <div className="space-y-4">
            <div className="h-3 w-36 rounded bg-white/5"></div>
            <div className="flex flex-col gap-3">
                {[1, 2].map((idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 sm:p-4 rounded-xl border border-white/5 bg-white/[0.02] gap-3 sm:gap-4">
                        <div className="flex items-center gap-3 sm:gap-4 min-w-0 flex-1">
                            {/* Image placeholder */}
                            <div className="w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 rounded-xl bg-white/5 border border-white/10 shrink-0"></div>
                            {/* Title & category & platform */}
                            <div className="min-w-0 flex flex-col justify-center gap-1.5 flex-1">
                                <div className="flex items-center gap-1.5 flex-wrap">
                                    <div className="h-4 w-16 sm:w-20 rounded bg-white/5"></div>
                                    <div className="h-4 w-14 sm:w-16 rounded bg-white/5"></div>
                                </div>
                                <div className="h-4 w-28 sm:w-44 rounded bg-white/5"></div>
                                <div className="hidden sm:block h-3 w-48 sm:w-64 rounded bg-white/5"></div>
                            </div>
                        </div>
                        {/* Price & Action */}
                        <div className="text-right flex-shrink-0 flex flex-col items-end justify-center ml-2 gap-2">
                            <div className="h-4 w-12 sm:w-16 rounded bg-white/5"></div>
                            <div className="h-6 w-14 sm:w-18 rounded-lg bg-white/5 border border-white/5"></div>
                        </div>
                    </div>
                ))}
            </div>
        </div>

        {/* Invoice-style Pricing Summary Box Skeleton */}
        <div className="flex justify-end pt-2 border-t border-white/5">
            <div className="w-full sm:w-72 rounded-2xl bg-[#050512]/80 border border-cyber-blue/20 p-4 space-y-2.5">
                {/* Subtotal skeleton */}
                <div className="flex justify-between items-center">
                    <div className="h-3 w-14 rounded bg-white/5"></div>
                    <div className="h-3 w-12 rounded bg-white/5"></div>
                </div>
                {/* Tax skeleton */}
                <div className="flex justify-between items-center">
                    <div className="h-3 w-10 rounded bg-white/5"></div>
                    <div className="h-3 w-10 rounded bg-white/5"></div>
                </div>
                {/* Divider line */}
                <div className="h-px bg-gradient-to-r from-cyber-blue/30 via-cyber-purple/20 to-transparent"></div>
                {/* Total Due skeleton */}
                <div className="flex justify-between items-center">
                    <div className="h-3.5 w-16 rounded bg-white/10"></div>
                    <div className="h-6 w-20 rounded bg-cyber-blue/20"></div>
                </div>
            </div>
        </div>
    </div>
);

export default function OrderHistory() {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { user } = useSelector((s) => s.auth);
    const { myOrders: orders, loading, pagination } = useSelector((s) => s.order);
    const { userReviews } = useSelector((s) => s.review);

    const [selectedGame, setSelectedGame] = useState(null);
    const [openRateModal, setOpenRateModal] = useState(false);
    const [hoverRating, setHoverRating] = useState(0);
    const [isFetchingMore, setIsFetchingMore] = useState(false);
    const [sentinelNode, setSentinelNode] = useState(null);

    // Callback ref so React notifies when the sentinel DOM node mounts
    const observerTargetRef = useCallback((node) => {
        setSentinelNode(node);
    }, []);

    const containerRef = useRef(null);

    // Load next page function
    const loadMoreOrders = useCallback(async () => {
        if (loading || isFetchingMore || !pagination || pagination.currentPage >= pagination.totalPages) return;
        setIsFetchingMore(true);
        try {
            await dispatch(fetchMyOrders({ page: pagination.currentPage + 1, limit: 5 })).unwrap();
        } catch (err) {
            console.error("Failed to load more orders:", err);
        } finally {
            setIsFetchingMore(false);
        }
    }, [dispatch, loading, isFetchingMore, pagination]);

    // High performance IntersectionObserver using callback ref sentinelNode
    useEffect(() => {
        if (!sentinelNode) return;

        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting && pagination && pagination.currentPage < pagination.totalPages && !loading && !isFetchingMore) {
                    loadMoreOrders();
                }
            },
            { threshold: 0.1, rootMargin: '300px' }
        );

        observer.observe(sentinelNode);
        return () => {
            observer.unobserve(sentinelNode);
        };
    }, [sentinelNode, pagination, loading, isFetchingMore, loadMoreOrders]);

    // Handle Infinite scroll backup listener (Trigger early before reaching footer)
    useEffect(() => {
        const handleScroll = () => {
            if (loading || isFetchingMore) return;
            const threshold = 600;
            const isNearBottom = window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - threshold;
            if (isNearBottom && pagination && pagination.currentPage < pagination.totalPages) {
                loadMoreOrders();
            }
        };

        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => window.removeEventListener('scroll', handleScroll);
    }, [pagination, loading, isFetchingMore, loadMoreOrders]);

    // Auto-fill screen if initial orders don't fill the viewport and more pages exist
    useEffect(() => {
        if (loading || isFetchingMore) return;
        if (pagination && pagination.currentPage < pagination.totalPages) {
            const isContentShort = document.documentElement.scrollHeight <= window.innerHeight + 400;
            if (isContentShort) {
                loadMoreOrders();
            }
        }
    }, [orders.length, pagination, loading, isFetchingMore, loadMoreOrders]);

    const handleReviewSubmit = async (values, { setSubmitting, resetForm }) => {
        if (!selectedGame) return;
        const gameId = selectedGame._id || selectedGame.id;
        const existingReview = userReviews?.find(rev => (rev.game?._id || rev.game) === gameId);
        try {
            if (existingReview) {
                await dispatch(updateReview({
                    reviewId: existingReview._id || existingReview.id,
                    rating: values.rating,
                    comment: values.comment
                })).unwrap();
                toast.success(`Your review for ${selectedGame.title} has been updated and submitted for approval.`, {
                    style: {
                        background: '#070710',
                        color: '#00f0ff',
                        border: '1px solid rgba(0, 240, 255, 0.2)',
                        fontSize: '11px',
                    }
                });
            } else {
                await dispatch(submitReview({ gameId, rating: values.rating, comment: values.comment })).unwrap();
                toast.success(`Thank you for rating ${selectedGame.title}! Your review has been submitted for approval.`, {
                    style: {
                        background: '#070710',
                        color: '#00f0ff',
                        border: '1px solid rgba(0, 240, 255, 0.2)',
                        fontSize: '11px',
                    }
                });
            }
            setOpenRateModal(false);
            resetForm();
        } catch (err) {
            toast.error(err || "Failed to submit review");
        } finally {
            setSubmitting(false);
        }
    };

    useEffect(() => {
        if (openRateModal) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => {
            document.body.style.overflow = '';
        };
    }, [openRateModal]);

    useEffect(() => {
        gsap.fromTo(containerRef.current,
            { opacity: 0, y: 30 },
            { opacity: 1, y: 0, duration: 0.6, ease: 'power3.out' }
        );
    }, []);

    useEffect(() => {
        dispatch(fetchMyOrders({ page: 1, limit: 5 }));
        if (user?._id || user?.id) {
            dispatch(fetchUserReviews(user._id || user.id));
        }
    }, [dispatch, user]);

    return (
        <div className="min-h-screen bg-transparent text-gray-100 pt-24 pb-12 sm:pt-32 sm:pb-32 relative overflow-hidden flex flex-col items-center">
            {/* Dynamic Ambient Platform Lighting with smooth feathered fade */}
            <div
                className="absolute inset-0 pointer-events-none overflow-hidden"
                style={{
                    maskImage: 'linear-gradient(to bottom, black 75%, transparent 100%)',
                    WebkitMaskImage: 'linear-gradient(to bottom, black 75%, transparent 100%)'
                }}
            >
                <div className="absolute top-0 left-0 w-full h-full bg-cyber-grid opacity-[0.04]" />
                <div
                    className="absolute top-[8%] left-1/2 -translate-x-1/2 w-[70vw] max-w-[900px] h-[400px] rounded-full blur-[140px] opacity-20"
                    style={{ backgroundColor: '#00f0ff' }}
                />
                <div className="absolute top-[30%] right-[-10%] w-[45vw] h-[45vw] max-w-[600px] bg-cyber-purple/10 rounded-full blur-[150px]" />
                <div className="absolute bottom-[10%] left-[-10%] w-[45vw] h-[45vw] max-w-[600px] bg-cyber-blue/10 rounded-full blur-[150px]" />
            </div>

            <div ref={containerRef} className="container w-full relative z-10 mx-auto px-3 xs:px-4 sm:px-6 lg:px-8">
                {/* Page Header */}
                <div className="mb-4 sm:mb-6 flex flex-col md:flex-row md:items-end justify-between gap-4 sm:gap-6 relative z-50">
                    <div className="flex-shrink-0">
                        <button
                            onClick={() => navigate(-1)}
                            className="inline-flex items-center justify-center gap-2 mb-4 px-3.5 sm:px-4 py-2 sm:py-2.5 rounded-xl bg-[#151a26] border border-white/[0.08] text-xs font-cyber font-bold text-gray-200 hover:text-white hover:border-white/20 hover:bg-[#1a2130] transition-all duration-300 uppercase tracking-wider group/btn shadow-sm"
                        >
                            <ArrowLeft className="w-3.5 h-3.5 group-hover/btn:-translate-x-1 transition-transform" />
                            <span>BACK</span>
                        </button>
                        <p className="text-[10px] font-cyber font-semibold text-cyber-cyan uppercase tracking-[0.25em] mb-2 flex items-center gap-2">
                            <span className="w-1.5 h-1.5 rounded-full bg-cyber-cyan shadow-[0_0_6px_#00f0ff] animate-pulse"></span>
                            Purchase History
                        </p>
                        <h1 className="font-cyber font-semibold text-3xl sm:text-4xl lg:text-5xl text-white uppercase tracking-tight whitespace-nowrap">
                            ORDER <span className="animate-gradient-text drop-shadow-[0_0_20px_rgba(0,240,255,0.35)]">HISTORY</span>
                        </h1>
                        <div className="h-[2px] w-20 animate-gradient-bg mt-3 rounded-full"></div>
                    </div>
                </div>

                {loading ? (
                    <div className="space-y-6 w-full">
                        <OrderCardSkeleton />
                        <OrderCardSkeleton />
                        <OrderCardSkeleton />
                    </div>
                ) : orders.length === 0 ? (
                    /* Premium Empty State with Astronaut Illustration UI */
                    <div className="flex flex-col items-center justify-center py-8 sm:py-12 text-center max-w-lg mx-auto space-y-4 px-4">
                        <div className="relative group w-full flex items-center justify-center p-1 sm:p-2">
                            <img
                                src={orderAstronautImg}
                                alt="Order History Empty Astronaut"
                                className="w-36 xs:w-44 sm:w-52 md:w-56 max-w-[240px] max-h-[250px] h-auto object-contain animate-astronaut-float cursor-pointer transition-transform duration-500 group-hover:scale-105"
                            />
                        </div>

                        <div className="space-y-2.5 max-w-md">
                            <h2 className="font-cyber font-black text-2xl md600:text-4xl text-[#00f0ff] uppercase tracking-wider drop-shadow-[0_0_18px_rgba(0,240,255,0.4)]">
                                NO ORDERS YET
                            </h2>
                            <p className="text-xs md600:text-sm text-gray-300 font-sans leading-relaxed">
                                You have not placed any orders yet. Browse our store to license your favorite titles.
                            </p>
                        </div>

                        <button
                            onClick={() => navigate('/all-games')}
                            className="w-full sm:w-auto relative overflow-hidden inline-flex items-center justify-center gap-2.5 px-6 py-2.5 sm:px-7 sm:py-3 rounded-xl bg-gradient-to-r from-[#00f0ff] via-[#5c60ff] to-[#bd00ff] text-black font-cyber font-bold text-xs sm:text-[13px] uppercase tracking-wider hover:scale-105 active:scale-95 transition-all duration-300 whitespace-nowrap group before:absolute before:top-0 before:-left-[100%] before:w-full before:h-full before:bg-gradient-to-r before:from-transparent before:via-white/40 before:to-transparent before:transition-all before:duration-700 hover:before:left-[100%]"
                        >
                            <span>DISCOVER GAMES</span>
                            <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                        </button>
                    </div>
                ) : (
                    <div className="space-y-6">
                        <style>{`
                            @keyframes cyber-scan-cyan {
                                0% { transform: translateY(-100%); }
                                100% { transform: translateY(220%); }
                            }
                            .animate-cyber-scan-cyan {
                                animation: cyber-scan-cyan 3s cubic-bezier(0.4, 0, 0.2, 1) infinite;
                            }
                            .cyber-grid-card {
                                background-image: radial-gradient(rgba(0, 240, 255, 0.03) 1px, transparent 1px);
                                background-size: 12px 12px;
                            }
                        `}</style>
                        {orders.map((order) => {
                            const orderDate = new Date(order.createdAt).toLocaleDateString('en-US', {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric'
                            });

                            return (
                                <div key={order._id} className="relative rounded-2xl border border-white/10 bg-[#080814]/85 backdrop-blur-xl group transition-all duration-500 flex flex-col overflow-hidden p-3.5 xs:p-4 md:p-6 gap-5 sm:gap-6 cyber-grid-card">

                                    {/* Tech corner accent */}
                                    <div className="absolute top-0 left-0 w-full h-[2px] bg-gradient-to-r from-cyber-cyan via-cyber-blue to-cyber-purple opacity-40 transition-opacity duration-500"></div>

                                    {/* Order Details Header */}
                                    <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3.5 sm:gap-6 pb-4 sm:pb-5 border-b border-white/10 bg-white/[0.005]">
                                        {/* Left: ID and Date/Payment info */}
                                        <div className="flex flex-col gap-2 xs:gap-2.5">
                                            <div className="flex items-center gap-2 xs:gap-2.5">
                                                <span className="text-[9px] xs:text-[10px] font-cyber font-bold text-gray-400 uppercase tracking-widest bg-white/[0.03] border border-white/10 px-2.5 py-1 rounded-lg">
                                                    {`ID: ${order.orderId || (order._id ? `ORD-${order._id.toString().slice(-7).toUpperCase()}` : 'ORD-1220424')}`}
                                                </span>
                                                <span className="h-[1px] w-6 xs:w-8 bg-gradient-to-r from-cyber-cyan/40 to-transparent"></span>
                                            </div>

                                            <div className="flex flex-wrap items-center gap-2 xs:gap-2.5 sm:gap-3">
                                                <div className="flex items-center gap-1.5 xs:gap-2 text-xs text-gray-300 font-sans bg-white/[0.03] border border-white/10 px-2.5 xs:px-3 py-1.5 rounded-xl shrink-0">
                                                    <Calendar className="w-3.5 h-3.5 text-cyber-cyan shrink-0" />
                                                    <span className="font-cyber text-[9px] xs:text-[10px] font-bold tracking-wide text-gray-400 uppercase whitespace-nowrap">ORDERED:</span>
                                                    <span className="font-bold text-white font-cyber text-[10px] xs:text-[11px] whitespace-nowrap">{orderDate}</span>
                                                </div>
                                                {(order.stripePaymentIntentId || order.razorpayPaymentId || order.paymentId || order._id) && (
                                                    <div className="flex items-center gap-1.5 xs:gap-2 text-xs text-gray-300 font-sans bg-white/[0.03] border border-white/10 px-2.5 xs:px-3 py-1.5 rounded-xl max-w-full overflow-hidden">
                                                        <CreditCard className="w-3.5 h-3.5 text-cyber-purple shrink-0" />
                                                        <span className="font-cyber text-[9px] xs:text-[10px] font-bold tracking-wide text-gray-400 uppercase whitespace-nowrap shrink-0">PAYMENT ID:</span>
                                                        <span
                                                            className="font-mono text-white text-[10px] xs:text-[11px] truncate max-w-[100px] xs:max-w-[150px] sm:max-w-none"
                                                            title={order.stripePaymentIntentId || order.razorpayPaymentId || order.paymentId || `pi_${String(order._id).slice(-16)}`}
                                                        >
                                                            {order.stripePaymentIntentId || order.razorpayPaymentId || order.paymentId || `pi_${String(order._id).slice(-16)}`}
                                                        </span>
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        {/* Right: Status badge top, Price + Invoice bottom */}
                                        <div className="flex flex-col items-start lg:items-end gap-2 xs:gap-2.5">
                                            <div>
                                                <span className={`px-2.5 py-0.5 rounded-md text-[9px] xs:text-[10px] font-cyber font-bold uppercase tracking-widest border ${order.status === 'Cancelled'
                                                    ? 'bg-red-500/10 border-red-500/30 text-red-400'
                                                    : 'bg-cyber-green/10 border-cyber-green/50 text-cyber-green shadow-[0_0_10px_rgba(57,255,20,0.15)]'
                                                    }`}>
                                                    {order.status || 'COMPLETED'}
                                                </span>
                                            </div>

                                            <div className="flex items-center gap-3.5 sm:gap-5">
                                                <p className="text-xl xs:text-2xl sm:text-3xl font-cyber font-black text-white leading-none tracking-tight">
                                                    ${order.total?.toFixed(2)}
                                                </p>
                                                <button
                                                    onClick={() => downloadInvoicePdf(order, user)}
                                                    className="px-3 py-1.5 xs:px-3.5 sm:px-4 sm:py-2 rounded-xl bg-cyber-cyan/10 border border-cyber-cyan/50 text-cyber-cyan hover:bg-cyber-cyan/20 hover:border-cyber-cyan/70 hover:shadow-[0_0_15px_rgba(0,240,255,0.3)] font-cyber font-bold text-[10px] sm:text-[11px] uppercase tracking-wider flex items-center gap-1.5 transition-all duration-300 active:scale-95 cursor-pointer"
                                                >
                                                    <Receipt className="w-3.5 h-3.5" />
                                                    <span>INVOICE</span>
                                                </button>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Order Items */}
                                    <div className="space-y-4">
                                        <p className="text-[9px] font-cyber font-semibold text-gray-400 uppercase tracking-[0.2em]">
                                            Purchased Access Tokens
                                        </p>
                                        <div className="flex flex-col gap-3">
                                            {order.items.map((item, idx) => {
                                                const game = item.game;
                                                if (!game) return null;
                                                return (
                                                    <div key={idx} className="relative flex items-center justify-between p-3 sm:p-4 rounded-xl border border-white/5 bg-white/[0.02] hover:bg-white/[0.04] hover:border-cyber-cyan/30 transition-all duration-300 group/item gap-3 sm:gap-4">

                                                        {/* Game cover + Info */}
                                                        <div className="flex items-center gap-3 sm:gap-4 min-w-0 flex-1">
                                                            <div className="w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 rounded-xl overflow-hidden flex-shrink-0 border border-white/10 relative bg-white/5 cursor-pointer group-hover/item:border-cyber-cyan/40 transition-all duration-300">
                                                                {(game.cover_image || game.image) ? (
                                                                    <img
                                                                        src={game.cover_image?.url || game.cover_image?.lowUrl || game.image?.url || game.image}
                                                                        alt={game.title}
                                                                        className="w-full h-full object-cover group-hover/item:scale-105 transition-transform duration-500"
                                                                    />
                                                                ) : (
                                                                    <div className="w-full h-full flex items-center justify-center bg-white/[0.02]">
                                                                        <Package className="w-5 h-5 sm:w-6 sm:h-6 text-gray-700" />
                                                                    </div>
                                                                )}
                                                                {/* Scanline animated bar */}
                                                                <div className="absolute inset-0 bg-gradient-to-b from-transparent via-cyber-cyan/20 to-transparent -translate-y-full group-hover/item:animate-cyber-scan-cyan pointer-events-none"></div>
                                                            </div>

                                                            <div className="min-w-0 flex flex-col justify-center">
                                                                <div className="flex items-center gap-1.5 mb-1 flex-wrap">
                                                                    <span className="text-[9px] font-cyber font-bold text-cyber-purple bg-cyber-purple/15 px-2 py-0.5 rounded border border-cyber-purple/30 uppercase tracking-widest w-max">
                                                                        {game.category?.name || 'GAME TOKEN'}
                                                                    </span>
                                                                    {item.platform && (() => {
                                                                        const platKey = (item.platform || 'windows').toLowerCase();
                                                                        const Icon = PlatformIconMap[platKey] || Gamepad2;
                                                                        const platName = PlatformNameMap[platKey] || item.platform;
                                                                        const color = PlatformColorMap[platKey] || '#00f0ff';
                                                                        return (
                                                                            <span className="inline-flex items-center gap-1 text-[9px] font-cyber font-bold px-2 py-0.5 rounded bg-white/[0.04] border border-white/10 text-gray-300 uppercase tracking-wider">
                                                                                <Icon className="w-2.5 h-2.5 flex-shrink-0" style={{ color }} />
                                                                                <span className="truncate">{platName}</span>
                                                                            </span>
                                                                        );
                                                                    })()}
                                                                </div>
                                                                <h4 className="text-xs sm:text-sm font-cyber font-bold text-white uppercase tracking-wide truncate group-hover/item:text-cyber-cyan transition-colors duration-300">
                                                                    {game.title}
                                                                </h4>
                                                                <p className="hidden sm:block text-[10px] text-gray-400 font-sans truncate mt-0.5 max-w-xs md:max-w-md">
                                                                    {game.tagline || game.description?.substring(0, 60)}
                                                                </p>
                                                            </div>
                                                        </div>

                                                        {/* Price & Quantity Info */}
                                                        <div className="text-right flex-shrink-0 flex flex-col items-end justify-center ml-2 gap-2">
                                                            <span className="text-xs sm:text-sm font-cyber font-bold text-white">
                                                                ${item.price?.toFixed(2)}
                                                            </span>
                                                            {order.status !== 'Cancelled' && (
                                                                (() => {
                                                                    const review = userReviews?.find(rev => (rev.game?._id || rev.game) === (game._id || game.id));
                                                                    if (review) {
                                                                        return (
                                                                            <button
                                                                                type="button"
                                                                                onClick={() => {
                                                                                    setSelectedGame(game);
                                                                                    setOpenRateModal(true);
                                                                                }}
                                                                                className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[9px] font-cyber font-bold uppercase tracking-wider border bg-cyber-cyan/10 border-cyber-cyan/40 text-cyber-cyan hover:bg-cyber-cyan/20 hover:border-cyber-cyan/60 hover:shadow-[0_0_10px_rgba(0,240,255,0.2)] transition-all cursor-pointer"
                                                                            >
                                                                                <Star className="w-3 h-3 fill-cyber-cyan/20 text-cyber-cyan" />
                                                                                Edit Rate
                                                                            </button>
                                                                        );
                                                                    } else {
                                                                        return (
                                                                            <button
                                                                                type="button"
                                                                                onClick={() => {
                                                                                    setSelectedGame(game);
                                                                                    setOpenRateModal(true);
                                                                                }}
                                                                                className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[9px] font-cyber font-bold uppercase tracking-wider border bg-cyber-purple/10 border-cyber-purple/40 text-cyber-purple hover:bg-cyber-purple/20 hover:border-cyber-purple/60 hover:shadow-[0_0_10px_rgba(189,0,255,0.2)] transition-all cursor-pointer"
                                                                            >
                                                                                <Star className="w-3 h-3 text-cyber-purple" />
                                                                                Rate
                                                                            </button>
                                                                        );
                                                                    }
                                                                })()
                                                            )}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>

                                    {/* ── Invoice-style Pricing Summary ── */}
                                    {(() => {
                                        const subtotal = order.subtotal !== undefined
                                            ? order.subtotal
                                            : order.items.reduce((sum, item) => sum + Number(item.price || 0) * Number(item.quantity || 1), 0);
                                        const tax = order.tax !== undefined ? order.tax : subtotal * 0.1;
                                        const discount = order.discountAmount !== undefined ? order.discountAmount : 0;
                                        const total = order.total !== undefined ? order.total : subtotal + tax - discount;
                                        return (
                                            <div className="flex justify-end pt-2 border-t border-white/5">
                                                <div className="w-full sm:w-72 rounded-2xl bg-[#050512]/80 border border-cyber-blue/20 p-4 space-y-2.5">
                                                    {/* Subtotal */}
                                                    <div className="flex justify-between items-center text-[11px]">
                                                        <span className="text-gray-500 font-sans">Subtotal</span>
                                                        <span className="text-gray-300 font-cyber">${subtotal.toFixed(2)}</span>
                                                    </div>
                                                    {/* Tax */}
                                                    <div className="flex justify-between items-center text-[11px]">
                                                        <span className="text-gray-500 font-sans">Tax</span>
                                                        <span className="text-gray-300 font-cyber">${tax.toFixed(2)}</span>
                                                    </div>
                                                    {/* Discount — only show if non-zero */}
                                                    {discount > 0 && (
                                                        <div className="flex justify-between items-center text-[11px]">
                                                            <span className="flex items-center gap-1.5 text-cyber-green font-sans">
                                                                <span className="w-1.5 h-1.5 rounded-full bg-cyber-green inline-block"></span>
                                                                Discount
                                                                {order.couponCode && (
                                                                    <span className="ml-1 px-1.5 py-0.5 rounded text-[8px] font-cyber font-bold bg-cyber-green/10 border border-cyber-green/20 text-cyber-green uppercase tracking-wider">
                                                                        {order.couponCode}
                                                                    </span>
                                                                )}
                                                            </span>
                                                            <span className="text-cyber-green font-cyber font-bold">-${discount.toFixed(2)}</span>
                                                        </div>
                                                    )}
                                                    {/* Divider */}
                                                    <div className="h-px bg-gradient-to-r from-cyber-blue/30 via-cyber-purple/20 to-transparent"></div>
                                                    {/* Total */}
                                                    <div className="flex justify-between items-center">
                                                        <span className="text-[9px] font-cyber text-cyber-blue uppercase tracking-widest">Total Due</span>
                                                        <span className="text-lg font-cyber font-black text-cyber-blue">${total.toFixed(2)}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })()}
                                </div>
                            );
                        })}

                        {/* Sentinel target for IntersectionObserver Infinite Scroll */}
                        <div ref={observerTargetRef} className="h-6 w-full" />

                        {/* Subtle Scroll loading indicator */}
                        {isFetchingMore && (
                            <div className="flex justify-center mt-6 mb-8">
                                <div className="flex items-center gap-2.5 px-6 py-3 rounded-full bg-white/[0.02] border border-cyber-cyan/30 text-cyber-cyan font-cyber text-xs uppercase tracking-widest animate-pulse shadow-[0_0_15px_rgba(0,240,255,0.15)]">
                                    <Loader2 className="w-4 h-4 animate-spin text-cyber-cyan" />
                                    <span>LOADING MORE ORDERS...</span>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
            {/* Rate Game Modal outside the GSAP transform context */}
            {openRateModal && selectedGame && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md">
                    <div className="relative w-full max-w-md rounded-2xl p-6 max-h-[90vh] overflow-y-auto border border-cyber-purple/30"
                        style={{ background: 'linear-gradient(135deg, #0e0e1c 0%, #05050c 100%)', boxShadow: '0 0 40px rgba(189, 0, 255, 0.2)' }}>
                        <div className="absolute top-0 left-0 w-full h-[3px] bg-gradient-to-r from-cyber-purple to-cyber-pink"></div>
                        <h3 className="text-sm font-cyber font-medium text-cyber-purple uppercase tracking-widest mb-2 flex items-center gap-2">
                            <Star className="w-5 h-5" /> Rate & Review Game
                        </h3>
                        <p className="text-xs text-gray-400 font-sans mb-4">
                            Share your feedback for <span className="text-white font-semibold">{selectedGame.title}</span>
                        </p>

                        <Formik
                            initialValues={{
                                rating: (() => {
                                    const rev = userReviews?.find(r => (r.game?._id || r.game) === (selectedGame._id || selectedGame.id));
                                    return rev ? rev.rating : 5;
                                })(),
                                comment: (() => {
                                    const rev = userReviews?.find(r => (r.game?._id || r.game) === (selectedGame._id || selectedGame.id));
                                    return rev ? rev.comment : '';
                                })()
                            }}
                            validationSchema={ReviewSchema}
                            onSubmit={handleReviewSubmit}
                            enableReinitialize
                        >
                            {({ values, errors, touched, setFieldValue, isSubmitting }) => (
                                <Form className="space-y-4">
                                    <div className="flex flex-col items-center gap-2 py-4 bg-white/[0.01] border border-white/5 rounded-xl">
                                        <span className="text-[10px] font-cyber text-gray-500 uppercase tracking-wider">Select Rating</span>
                                        <div className="flex items-center gap-1.5">
                                            {[1, 2, 3, 4, 5].map((star) => (
                                                <button
                                                    key={star}
                                                    type="button"
                                                    onClick={() => setFieldValue('rating', star)}
                                                    onMouseEnter={() => setHoverRating(star)}
                                                    onMouseLeave={() => setHoverRating(0)}
                                                    className="p-1 transition-transform hover:scale-125 focus:outline-none cursor-pointer"
                                                >
                                                    <Star
                                                        className={`w-8 h-8 transition-colors ${star <= (hoverRating || values.rating)
                                                                ? 'text-amber-400 fill-amber-400 filter drop-shadow-[0_0_8px_rgba(251,191,36,0.5)]'
                                                                : 'text-gray-700'
                                                            }`}
                                                    />
                                                </button>
                                            ))}
                                        </div>
                                        <span className="text-xs font-cyber font-bold text-amber-400 mt-1">
                                            {hoverRating || values.rating} / 5 ({
                                                (hoverRating || values.rating) === 5 ? 'Masterpiece' :
                                                    (hoverRating || values.rating) === 4 ? 'Great' :
                                                        (hoverRating || values.rating) === 3 ? 'Good' :
                                                            (hoverRating || values.rating) === 2 ? 'Mediocre' : 'Awful'
                                            })
                                        </span>
                                        {errors.rating && touched.rating && (
                                            <p className="text-[10px] text-red-500 mt-1">{errors.rating}</p>
                                        )}
                                    </div>

                                    <div className="space-y-1.5">
                                        <label className="text-[10px] font-cyber font-medium text-gray-500 uppercase tracking-widest">Review Comments</label>
                                        <Field
                                            as="textarea"
                                            name="comment"
                                            rows={4}
                                            placeholder="Write your constructive thoughts about this game..."
                                            className={`w-full px-4 py-3 rounded-xl text-xs md:text-sm font-cyber text-white bg-white/[0.04] border border-white/10 focus:border-cyber-purple/50 focus:outline-none transition-all placeholder:text-gray-700 resize-none ${errors.comment && touched.comment ? 'border-red-500/50 focus:border-red-500/50' : ''
                                                }`}
                                        />
                                        {errors.comment && touched.comment && (
                                            <p className="text-[10px] text-red-500">{errors.comment}</p>
                                        )}
                                    </div>

                                    <div className="flex justify-end gap-3 pt-2">
                                        <button
                                            type="button"
                                            onClick={() => setOpenRateModal(false)}
                                            className="px-4 py-2.5 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 text-gray-300 text-xs font-cyber transition-all"
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="submit"
                                            disabled={isSubmitting}
                                            className="px-6 py-2.5 rounded-xl bg-cyber-purple hover:bg-cyber-purple/80 text-white text-xs font-cyber font-bold transition-all shadow-[0_0_15px_rgba(189,0,255,0.3)] cursor-pointer disabled:opacity-50"
                                        >
                                            {isSubmitting ? 'Submitting...' : 'Submit Review'}
                                        </button>
                                    </div>
                                </Form>
                            )}
                        </Formik>
                    </div>
                </div>
            )}
        </div>
    );
}
